import React, { type JSX,useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import {
    Autocomplete,
    FormControl,
    MenuItem,
    TextField,
    Typography,
} from '@mui/material';
import { useAppDispatch } from 'app/hooks';
import { extractStatusCode } from 'common/services/api/apiHelpers';
import { fetchTenantUserPaginatedMemberships } from 'common/services/api/TenantUserMembership/TenantUserMembership.thunk';
import { handleListFetchError } from 'common/utils/errorHandlers';
import { useSnackbar } from 'notistack';

const NOTIFY_MESSAGES = {
    EXPLANATION_MESSAGE: 'User must be added to Tenant to show up in results is shown',
    SHOW_MORE_MESSAGE: 'There were more than 25 results, please update your search criteria to limit the result set',
};
const DISPLAYED_ROWS_COUNT = 25;

enum MembershipType {
    ROLE = 'excludeRoleId',
    CLAIM = 'excludeClaimId',
    USERGROUP = 'excludeGroupId',
    RESOURCE = 'excludeAclResourceId'
}

export interface IUserProfileSearchDropdownProps {
    value: IUser | null;
    onChange: (e: unknown, userInfo: IUser | null) => void;
    itemIdToExclude?: string;
    membershipType?: `${MembershipType}`;
    hasError?: boolean;
}
export interface IUser {
    index?: number;
    id: string;
    firstName?: string;
    lastName?: string;
    companyKey?: string;
    globalRoles?: string;
    isActive?: string;
    status?: string;
    rawGlobalRoles?: {
        id?: string;
        name?: string;
    }[];
    profile: {
        firstName: string;
        lastName: string;
        principalName: string;
        email: string;
        companyKey: string;
    };
}

export function UserProfileSearchDropdown(props: Readonly<IUserProfileSearchDropdownProps>): JSX.Element {
    const [userProfiles, setUserProfiles] = useState<IUser[]>([]);
    const [inputValue, setInputValue] = useState<string>('');
    const [processing, setProcessing] = useState<boolean>(false);
    const [fetched, setFetched] = useState<boolean>(false);
    const dispatch = useAppDispatch();
    const { tenantId } = useParams();
    const { enqueueSnackbar } = useSnackbar();

    const { value, onChange } = props;

    const handleInputValue = (_e: unknown, newValue: string) => {
        setInputValue(newValue);
    };

    const handleKeyDown: React.KeyboardEventHandler<HTMLDivElement> | undefined = e => {
        if (!inputValue) {
            return;
        }
        if (e.key === 'Enter') {
            setProcessing(true);
            setUserProfiles([]);
            dispatch(fetchTenantUserPaginatedMemberships({
                tenantId: tenantId ?? '',
                skip: 0,
                take: DISPLAYED_ROWS_COUNT,
                search: inputValue.trim(),
            }))
                .unwrap()
                .then((response) => {
                    setUserProfiles(response.data);
                })
                .catch((error) => {
                    setUserProfiles([]);
                    setProcessing(false);
                    setFetched(false);
                    handleListFetchError(extractStatusCode(error.message), 'Tenant User Profiles', enqueueSnackbar);
                })
                .finally(() => {
                    setFetched(true);
                    setProcessing(false);
                });
        }
    };

    const handleUserSelect = (e: React.SyntheticEvent, newValue: IUser | null) => {
        onChange(null, newValue);
        setUserProfiles([]);
    };

    const handleClose = () => {
        if (!inputValue) {
            setUserProfiles([]);
            onChange?.(null, null);
        }
    };

    const options = useMemo(() => {
        return userProfiles.map((item, i )=> ({ ...item, index: i }));
    }, [userProfiles]);

    return (
        <FormControl>
            <Autocomplete
                handleHomeEndKeys={false}
                id='userProfile'
                options={options}
                value={value}
                loading={processing}
                filterOptions={x => x}
                onInputChange={handleInputValue}
                noOptionsText={fetched ? NOTIFY_MESSAGES.EXPLANATION_MESSAGE : 'Search by name, email, or company'}
                onKeyDown={handleKeyDown}
                onChange={handleUserSelect}
                onClose={handleClose}
                getOptionLabel={opt => `${opt.profile.firstName || ''} ${opt.profile.lastName}`}
                isOptionEqualToValue={() => true}
                renderInput={(params) => <TextField {...params} label="Tenant User Search" />}
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                renderOption={({ key: _, ...params }, opt) =>
                    <React.Fragment key={`${opt.profile.email}-${opt.id}`}>
                        {opt.index === 0 && userProfiles.length > 0 ?
                            <Typography color='info.light' sx={{ padding: '0px 15px 10px' }}>
                                {NOTIFY_MESSAGES.EXPLANATION_MESSAGE}
                            </Typography>
                            : null}
                        <MenuItem
                            data-testid='userOption'
                            {...params}
                            value={opt.profile.email}
                            style={{ display: 'block' }}
                        >
                            <Typography>{`${opt.profile.firstName || ''} ${opt.profile.lastName || ''}`}</Typography>
                            <Typography
                                variant="body1"
                                gutterBottom
                                color="info.light"
                                noWrap
                            >
                                {opt.profile.email || ''}
                            </Typography>
                        </MenuItem>
                        {opt.index === DISPLAYED_ROWS_COUNT - 1 && userProfiles.length === DISPLAYED_ROWS_COUNT ?
                            <Typography color='info.light' sx={{ padding: '0 15px 5px' }}>
                                {NOTIFY_MESSAGES.SHOW_MORE_MESSAGE}
                            </Typography>
                            : null}
                    </React.Fragment>
                }
            />
        </FormControl>
    );
}
